import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Store,
  Users,
  LogOut,
  Plus,
  Shield,
  Eye,
  Calendar,
  DollarSign,
  Search,
  RefreshCw,
  MoreVertical,
  Power,
  PowerOff,
  Trash2,
  Settings,
  Package,
  BarChart3,
  Clock,
  MessageCircle,
  CreditCard,
  Mail,
  Plug
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Moduli disponibili
const AVAILABLE_MODULES = [
  { code: 'BASE', name: 'Base', description: 'Prenotazioni e gestione appuntamenti', icon: Calendar, required: true },
  { code: 'SHOP', name: 'E-commerce', description: 'Vendita prodotti online', icon: Package },
  { code: 'REPORTS_ADV', name: 'Report Avanzati', description: 'Analytics e statistiche dettagliate', icon: BarChart3 },
  { code: 'STAFF_PRESENCE', name: 'Presenze Staff', description: 'Timbrature e gestione ore lavorate', icon: Clock },
  { code: 'WHATSAPP', name: 'WhatsApp', description: 'Notifiche automatiche via WhatsApp', icon: MessageCircle },
];

export default function PlatformAdminStores() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [newStore, setNewStore] = useState({ name: '', slug: '', adminEmail: '', adminName: '', adminPassword: '' });
  const [creatingStore, setCreatingStore] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, store: null, action: null });
  const [modulesDialog, setModulesDialog] = useState({ open: false, store: null, modules: [] });
  const [savingModules, setSavingModules] = useState(false);
  const [integrationsDialog, setIntegrationsDialog] = useState({ open: false, store: null, data: null });
  const [savingIntegration, setSavingIntegration] = useState(false);

  useEffect(() => {
    // Verifica che l'utente sia Platform Admin
    if (!user || user.role !== 'ADMIN_PLATFORM') {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [storesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stores`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/stats`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setStores(storesRes.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Pulisci lo stato di impersonificazione
    localStorage.removeItem('platform_admin_impersonating');
    localStorage.removeItem('platform_admin_store_slug');
    logout();
    navigate('/');
  };

  const handleEnterStore = (store) => {
    // Salva lo stato di impersonificazione
    localStorage.setItem('platform_admin_impersonating', 'true');
    localStorage.setItem('platform_admin_store_slug', store.slug);
    localStorage.setItem('storeSlug', store.slug);
    
    toast.success(`Accesso al negozio: ${store.name}`);
    // Forza un reload per aggiornare il contesto del negozio
    window.location.href = '/admin/store';
  };

  const handleCreateStore = async () => {
    if (!newStore.name || !newStore.slug) {
      toast.error('Inserisci nome e slug del negozio');
      return;
    }
    
    setCreatingStore(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Crea il negozio
      const storeRes = await axios.post(`${API_URL}/api/stores`, {
        name: newStore.name,
        slug: newStore.slug.toLowerCase().replace(/\s+/g, '-'),
        status: 'ATTIVO'
      }, { headers });
      
      // Se sono stati inseriti i dati admin, crea anche l'admin
      if (newStore.adminEmail && newStore.adminName && newStore.adminPassword) {
        try {
          await axios.post(`${API_URL}/api/stores/${storeRes.data.id}/admin`, {
            name: newStore.adminName,
            email: newStore.adminEmail,
            password: newStore.adminPassword
          }, { headers });
          toast.success('Negozio e admin creati con successo!');
        } catch (adminErr) {
          toast.warning('Negozio creato, ma errore nella creazione admin');
        }
      } else {
        toast.success('Negozio creato con successo!');
      }
      
      setWizardOpen(false);
      setNewStore({ name: '', slug: '', adminEmail: '', adminName: '', adminPassword: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del negozio');
    } finally {
      setCreatingStore(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DEMO': { color: 'bg-blue-500', label: 'Demo' },
      'ATTIVO': { color: 'bg-green-500', label: 'Attivo' },
      'ACTIVE': { color: 'bg-green-500', label: 'Attivo' },
      'SOSPESO': { color: 'bg-yellow-500', label: 'Sospeso' },
      'SUSPENDED': { color: 'bg-yellow-500', label: 'Sospeso' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', label: status };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const isStoreActive = (status) => {
    return status === 'ATTIVO' || status === 'ACTIVE' || status === 'DEMO';
  };

  const handleToggleStoreStatus = async (store) => {
    const newStatus = isStoreActive(store.status) ? 'SUSPENDED' : 'ACTIVE';
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/stores/${store.id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newStatus === 'ACTIVE' ? 'Negozio attivato' : 'Negozio sospeso');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
    }
    setConfirmDialog({ open: false, store: null, action: null });
  };

  const handleDeleteStore = async (store) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/stores/${store.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Negozio eliminato');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella eliminazione');
    }
    setConfirmDialog({ open: false, store: null, action: null });
  };

  const openConfirmDialog = (store, action) => {
    setConfirmDialog({ open: true, store, action });
  };

  const openModulesDialog = (store) => {
    const currentModules = store.modules || store.active_modules || ['BASE'];
    setModulesDialog({ open: true, store, modules: [...currentModules] });
  };

  const toggleModule = (moduleCode) => {
    if (moduleCode === 'BASE') return; // BASE è sempre richiesto
    
    setModulesDialog(prev => {
      const modules = prev.modules.includes(moduleCode)
        ? prev.modules.filter(m => m !== moduleCode)
        : [...prev.modules, moduleCode];
      return { ...prev, modules };
    });
  };

  const handleSaveModules = async () => {
    if (!modulesDialog.store) return;
    
    setSavingModules(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/stores/${modulesDialog.store.id}/modules`,
        modulesDialog.modules,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Moduli aggiornati');
      setModulesDialog({ open: false, store: null, modules: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio moduli');
    } finally {
      setSavingModules(false);
    }
  };

  const openIntegrationsDialog = async (store) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/integrations/${store.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationsDialog({ open: true, store, data: response.data });
    } catch (error) {
      toast.error('Errore nel caricamento integrazioni');
    }
  };

  const handleSaveIntegration = async (type, config) => {
    if (!integrationsDialog.store) return;
    
    setSavingIntegration(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/integrations/${integrationsDialog.store.id}/${type}`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Configurazione ${type.toUpperCase()} salvata`);
      
      // Reload integrations data
      const response = await axios.get(`${API_URL}/api/integrations/${integrationsDialog.store.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrationsDialog(prev => ({ ...prev, data: response.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingIntegration(false);
    }
  };

  const filteredStores = stores.filter(store => 
    store.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  if (!user || user.role !== 'ADMIN_PLATFORM') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">AppLook Platform</h1>
                <p className="text-xs text-slate-400">Gestione Piattaforma</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-300 hidden sm:block">{user?.name}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Negozi Totali</p>
                  <p className="text-2xl font-bold text-white">{stores.length}</p>
                </div>
                <Store className="w-8 h-8 text-emerald-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Utenti</p>
                  <p className="text-2xl font-bold text-white">{stats.users || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Appuntamenti</p>
                  <p className="text-2xl font-bold text-white">{stats.appointments || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Fatturato</p>
                  <p className="text-2xl font-bold text-white">{formatPrice(stats.revenue)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stores Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Store className="w-5 h-5 text-emerald-500" />
                  Negozi Registrati
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Seleziona un negozio per accedere alla sua dashboard
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={loadData}
                  disabled={loading}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Aggiorna
                </Button>
                <Button 
                  onClick={() => setWizardOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Negozio
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca negozio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>

            {/* Store List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : filteredStores.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                {searchQuery ? 'Nessun negozio trovato' : 'Nessun negozio registrato'}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredStores.map(store => (
                  <div 
                    key={store.id} 
                    className={`group flex items-center justify-between p-4 rounded-lg border transition-all ${
                      isStoreActive(store.status) 
                        ? 'bg-slate-700/30 border-slate-600/50 hover:border-emerald-500/50 hover:bg-slate-700/50' 
                        : 'bg-slate-800/50 border-red-900/30 opacity-60'
                    }`}
                    data-testid={`store-item-${store.slug}`}
                  >
                    <div className="flex items-center gap-4 cursor-pointer" onClick={() => handleEnterStore(store)}>
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${
                        isStoreActive(store.status)
                          ? 'bg-gradient-to-br from-emerald-500/20 to-teal-600/20 border-emerald-500/30'
                          : 'bg-slate-700/50 border-slate-600'
                      }`}>
                        {store.branding?.logo_url ? (
                          <img src={store.branding.logo_url} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <Store className={`w-6 h-6 ${isStoreActive(store.status) ? 'text-emerald-500' : 'text-slate-500'}`} />
                        )}
                      </div>
                      <div>
                        <p className={`font-semibold transition-colors ${
                          isStoreActive(store.status) 
                            ? 'text-white group-hover:text-emerald-400' 
                            : 'text-slate-400'
                        }`}>
                          {store.name}
                        </p>
                        <p className="text-sm text-slate-400">/{store.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(store.status)}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnterStore(store);
                        }}
                        disabled={!isStoreActive(store.status)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Accedi
                      </Button>
                      
                      {/* Menu Azioni */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-slate-400 hover:text-white hover:bg-slate-600"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              openModulesDialog(store);
                            }}
                            className="text-blue-400 focus:text-blue-300 focus:bg-blue-500/10 cursor-pointer"
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Gestisci Moduli
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              openIntegrationsDialog(store);
                            }}
                            className="text-purple-400 focus:text-purple-300 focus:bg-purple-500/10 cursor-pointer"
                          >
                            <Plug className="w-4 h-4 mr-2" />
                            Integrazioni
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-700" />
                          {isStoreActive(store.status) ? (
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfirmDialog(store, 'suspend');
                              }}
                              className="text-yellow-400 focus:text-yellow-300 focus:bg-yellow-500/10 cursor-pointer"
                            >
                              <PowerOff className="w-4 h-4 mr-2" />
                              Sospendi Negozio
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfirmDialog(store, 'activate');
                              }}
                              className="text-green-400 focus:text-green-300 focus:bg-green-500/10 cursor-pointer"
                            >
                              <Power className="w-4 h-4 mr-2" />
                              Attiva Negozio
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator className="bg-slate-700" />
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmDialog(store, 'delete');
                            }}
                            className="text-red-400 focus:text-red-300 focus:bg-red-500/10 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina Negozio
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* New Store Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-500" />
              Nuovo Negozio
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Crea un nuovo negozio sulla piattaforma
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Nome Negozio *</Label>
              <Input 
                value={newStore.name} 
                onChange={(e) => setNewStore({...newStore, name: e.target.value})}
                placeholder="Es: Barber Shop Milano"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Slug (URL) *</Label>
              <Input 
                value={newStore.slug} 
                onChange={(e) => setNewStore({...newStore, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                placeholder="Es: barber-shop-milano"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500">Sarà usato nell&apos;URL: /s/{newStore.slug || 'slug'}</p>
            </div>
            
            <div className="border-t border-slate-700 pt-4 mt-4">
              <p className="text-sm text-slate-400 mb-3">Admin del negozio (opzionale)</p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nome Admin</Label>
                  <Input 
                    value={newStore.adminName} 
                    onChange={(e) => setNewStore({...newStore, adminName: e.target.value})}
                    placeholder="Nome completo"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Admin</Label>
                  <Input 
                    type="email"
                    value={newStore.adminEmail} 
                    onChange={(e) => setNewStore({...newStore, adminEmail: e.target.value})}
                    placeholder="admin@negozio.it"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Password Admin</Label>
                  <Input 
                    type="password"
                    value={newStore.adminPassword} 
                    onChange={(e) => setNewStore({...newStore, adminPassword: e.target.value})}
                    placeholder="••••••••"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleCreateStore} 
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={creatingStore || !newStore.name || !newStore.slug}
            >
              {creatingStore ? 'Creazione...' : 'Crea Negozio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, store: null, action: null })}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {confirmDialog.action === 'delete' && <Trash2 className="w-5 h-5 text-red-500" />}
              {confirmDialog.action === 'suspend' && <PowerOff className="w-5 h-5 text-yellow-500" />}
              {confirmDialog.action === 'activate' && <Power className="w-5 h-5 text-green-500" />}
              {confirmDialog.action === 'delete' && 'Elimina Negozio'}
              {confirmDialog.action === 'suspend' && 'Sospendi Negozio'}
              {confirmDialog.action === 'activate' && 'Attiva Negozio'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {confirmDialog.action === 'delete' && (
                <>Sei sicuro di voler eliminare <strong className="text-white">{confirmDialog.store?.name}</strong>? Questa azione non può essere annullata.</>
              )}
              {confirmDialog.action === 'suspend' && (
                <>Sei sicuro di voler sospendere <strong className="text-white">{confirmDialog.store?.name}</strong>? Il negozio non sarà accessibile ai clienti.</>
              )}
              {confirmDialog.action === 'activate' && (
                <>Sei sicuro di voler riattivare <strong className="text-white">{confirmDialog.store?.name}</strong>?</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setConfirmDialog({ open: false, store: null, action: null })}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Annulla
            </Button>
            <Button 
              onClick={() => {
                if (confirmDialog.action === 'delete') {
                  handleDeleteStore(confirmDialog.store);
                } else {
                  handleToggleStoreStatus(confirmDialog.store);
                }
              }}
              className={`flex-1 ${
                confirmDialog.action === 'delete' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : confirmDialog.action === 'suspend'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {confirmDialog.action === 'delete' && 'Elimina'}
              {confirmDialog.action === 'suspend' && 'Sospendi'}
              {confirmDialog.action === 'activate' && 'Attiva'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modules Management Dialog */}
      <Dialog open={modulesDialog.open} onOpenChange={(open) => !open && setModulesDialog({ open: false, store: null, modules: [] })}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-500" />
              Gestione Moduli
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Attiva o disattiva i moduli per <strong className="text-white">{modulesDialog.store?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {AVAILABLE_MODULES.map((module) => {
              const Icon = module.icon;
              const isActive = modulesDialog.modules.includes(module.code);
              const isRequired = module.required;
              
              return (
                <div 
                  key={module.code}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    isActive 
                      ? 'bg-slate-700/50 border-emerald-500/50' 
                      : 'bg-slate-800/50 border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-emerald-500/20' : 'bg-slate-700'
                    }`}>
                      <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${isActive ? 'text-white' : 'text-slate-400'}`}>
                        {module.name}
                        {isRequired && <Badge className="ml-2 bg-slate-600 text-xs">Richiesto</Badge>}
                      </p>
                      <p className="text-xs text-slate-500">{module.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={() => toggleModule(module.code)}
                    disabled={isRequired}
                    className="data-[state=checked]:bg-emerald-500"
                  />
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 pt-4 border-t border-slate-700">
            <Button 
              variant="outline" 
              onClick={() => setModulesDialog({ open: false, store: null, modules: [] })}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Annulla
            </Button>
            <Button 
              onClick={handleSaveModules}
              disabled={savingModules}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {savingModules ? 'Salvataggio...' : 'Salva Moduli'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Integrations Management Dialog */}
      <Dialog open={integrationsDialog.open} onOpenChange={(open) => !open && setIntegrationsDialog({ open: false, store: null, data: null })}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plug className="w-5 h-5 text-purple-500" />
              Integrazioni - {integrationsDialog.store?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configura le API keys per i servizi esterni
            </DialogDescription>
          </DialogHeader>
          
          {integrationsDialog.data && (
            <div className="space-y-6 pt-4">
              {/* STRIPE */}
              <IntegrationCard
                title="Stripe"
                description="Pagamenti con carta di credito"
                icon={CreditCard}
                color="purple"
                enabled={integrationsDialog.data.stripe?.enabled}
                fields={[
                  { key: 'api_key', label: 'API Key (Secret)', value: integrationsDialog.data.stripe?.api_key || '', placeholder: 'sk_live_...' },
                  { key: 'webhook_secret', label: 'Webhook Secret', value: integrationsDialog.data.stripe?.webhook_secret || '', placeholder: 'whsec_...' }
                ]}
                onSave={(config) => handleSaveIntegration('stripe', config)}
                saving={savingIntegration}
              />

              {/* PAYPAL */}
              <IntegrationCard
                title="PayPal"
                description="Pagamenti con PayPal"
                icon={DollarSign}
                color="blue"
                enabled={integrationsDialog.data.paypal?.enabled}
                fields={[
                  { key: 'client_id', label: 'Client ID', value: integrationsDialog.data.paypal?.client_id || '', placeholder: 'Client ID' },
                  { key: 'client_secret', label: 'Client Secret', value: integrationsDialog.data.paypal?.client_secret || '', placeholder: 'Client Secret' }
                ]}
                extraFields={[
                  { key: 'sandbox', label: 'Modalità Sandbox (Test)', type: 'switch', value: integrationsDialog.data.paypal?.sandbox ?? true }
                ]}
                onSave={(config) => handleSaveIntegration('paypal', config)}
                saving={savingIntegration}
              />

              {/* TWILIO / WHATSAPP */}
              <IntegrationCard
                title="WhatsApp (Twilio)"
                description="Notifiche via WhatsApp"
                icon={MessageCircle}
                color="green"
                enabled={integrationsDialog.data.twilio?.enabled}
                fields={[
                  { key: 'account_sid', label: 'Account SID', value: integrationsDialog.data.twilio?.account_sid || '', placeholder: 'AC...' },
                  { key: 'auth_token', label: 'Auth Token', value: integrationsDialog.data.twilio?.auth_token || '', placeholder: 'Auth Token' },
                  { key: 'phone_number', label: 'Numero WhatsApp', value: integrationsDialog.data.twilio?.phone_number || '', placeholder: '+39...' }
                ]}
                onSave={(config) => handleSaveIntegration('twilio', config)}
                saving={savingIntegration}
              />

              {/* RESEND / EMAIL */}
              <IntegrationCard
                title="Email (Resend)"
                description="Notifiche via email"
                icon={Mail}
                color="orange"
                enabled={integrationsDialog.data.resend?.enabled}
                fields={[
                  { key: 'api_key', label: 'API Key', value: integrationsDialog.data.resend?.api_key || '', placeholder: 're_...' },
                  { key: 'from_email', label: 'Email Mittente', value: integrationsDialog.data.resend?.from_email || '', placeholder: 'noreply@tuodominio.it' },
                  { key: 'from_name', label: 'Nome Mittente', value: integrationsDialog.data.resend?.from_name || '', placeholder: 'Nome Negozio' }
                ]}
                onSave={(config) => handleSaveIntegration('resend', config)}
                saving={savingIntegration}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Integration Card Component
function IntegrationCard({ title, description, icon: Icon, color, enabled, fields, extraFields, onSave, saving }) {
  const [localEnabled, setLocalEnabled] = useState(enabled);
  const [localFields, setLocalFields] = useState(() => {
    const initial = {};
    fields.forEach(f => initial[f.key] = f.value);
    if (extraFields) extraFields.forEach(f => initial[f.key] = f.value);
    return initial;
  });

  React.useEffect(() => {
    setLocalEnabled(enabled);
    const newFields = {};
    fields.forEach(f => newFields[f.key] = f.value);
    if (extraFields) extraFields.forEach(f => newFields[f.key] = f.value);
    setLocalFields(newFields);
  }, [enabled, fields, extraFields]);

  const colorClasses = {
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    green: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' }
  };

  const colors = colorClasses[color] || colorClasses.purple;

  const handleSave = () => {
    onSave({ ...localFields, enabled: localEnabled });
  };

  return (
    <div className={`p-4 rounded-lg border ${localEnabled ? colors.border : 'border-slate-700'} ${localEnabled ? 'bg-slate-700/30' : 'bg-slate-800/30'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <Icon className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div>
            <h4 className="font-medium text-white">{title}</h4>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
        </div>
        <Switch
          checked={localEnabled}
          onCheckedChange={setLocalEnabled}
          className="data-[state=checked]:bg-emerald-500"
        />
      </div>

      {localEnabled && (
        <div className="space-y-3 pt-2 border-t border-slate-700">
          {fields.map(field => (
            <div key={field.key}>
              <Label className="text-xs text-slate-400">{field.label}</Label>
              <Input
                type={field.key.includes('secret') || field.key.includes('token') || field.key.includes('api_key') ? 'password' : 'text'}
                value={localFields[field.key] || ''}
                onChange={(e) => setLocalFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                className="bg-slate-700/50 border-slate-600 text-white mt-1"
              />
            </div>
          ))}
          {extraFields?.map(field => (
            <div key={field.key} className="flex items-center justify-between py-2">
              <Label className="text-sm text-slate-300">{field.label}</Label>
              <Switch
                checked={localFields[field.key] ?? true}
                onCheckedChange={(checked) => setLocalFields(prev => ({ ...prev, [field.key]: checked }))}
                className="data-[state=checked]:bg-emerald-500"
              />
            </div>
          ))}
          <Button 
            onClick={handleSave} 
            disabled={saving}
            size="sm"
            className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? 'Salvataggio...' : 'Salva Configurazione'}
          </Button>
        </div>
      )}
    </div>
  );
}
